import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Minus, Plus, ShoppingCart, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { StickyCTA } from "@/components/site/StickyCTA";
import { SHOP_CATEGORIES, SHOP_PRODUCTS } from "@/data/catalog";
import { TIME_SLOTS } from "@/data/services";

const LOGO_MARK = "/logo-red.jpg";

const ProductCard = ({ product, onBook }) => {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? product.bullets : product.bullets.slice(0, 4);
  const hiddenCount = product.bullets.length - shown.length;

  return (
    <div data-testid={`shop-product-${product.id}`} className="bg-white border border-gray-200 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="text-lg font-bold text-gray-900">{product.name}</h3>
          {product.varies && <p className="text-xs text-gray-400 mt-0.5">Pricing varies</p>}
          {product.duration && <p className="text-xs text-gray-400 mt-0.5">Estimated duration: {product.duration}</p>}
        </div>
        <div className="text-right shrink-0">
          <p className="text-lg font-bold text-gray-900">{product.price}</p>
          <p className="text-xs text-gray-400 mt-0.5">Qty: 1</p>
        </div>
      </div>

      {product.desc && <p className="mt-3 text-sm text-gray-600 leading-relaxed">{product.desc}</p>}

      {product.bullets.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Description:</p>
          <ul className="mt-1.5 space-y-1">
            {shown.map((b) =>
              b.startsWith("—") ? (
                <li key={b} className="text-xs font-bold text-gray-500 pt-2">{b}</li>
              ) : (
                <li key={b} className="text-sm text-gray-600 flex items-start gap-2">
                  <span className="text-gray-300 mt-1">•</span>
                  <span>{b}</span>
                </li>
              )
            )}
          </ul>
          {product.bullets.length > 4 && (
            <button
              data-testid={`shop-showmore-${product.id}`}
              onClick={() => setExpanded(!expanded)}
              className="mt-2 text-sm text-[#EA0A0B] hover:underline"
            >
              {expanded ? "Show less" : `Show more${hiddenCount > 0 ? ` (${hiddenCount} more)` : ""}`}
            </button>
          )}
        </div>
      )}

      <button
        data-testid={`shop-book-${product.id}`}
        onClick={() => onBook(product)}
        className="mt-5 inline-flex items-center gap-2 bg-[#EA0A0B] hover:bg-[#C20A0B] text-white font-display uppercase tracking-[0.1em] text-xs px-8 py-3 transition-colors"
      >
        <ShoppingCart className="w-4 h-4" /> Book This Service
      </button>
    </div>
  );
};

const BookingModal = ({ product, blockedDates, onClose }) => {
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", vehicle: "", notes: "", company: "" });
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [daySlots, setDaySlots] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);

  const today = new Date().toISOString().slice(0, 10);
  const isBlocked = date && blockedDates.includes(date);

  useEffect(() => {
    if (!date || isBlocked) {
      setDaySlots(null);
      setSlot("");
      return undefined;
    }
    let live = true;
    setSlotsLoading(true);
    api.get(`/availability?date=${date}`)
      .then(({ data }) => {
        if (!live) return;
        const slots = data.slots || [];
        setDaySlots(slots);
        setSlot((s) => (slots.includes(s) ? s : ""));
      })
      .catch(() => { if (live) setDaySlots(TIME_SLOTS); })
      .finally(() => { if (live) setSlotsLoading(false); });
    return () => { live = false; };
  }, [date, isBlocked]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !slot) {
      toast.error("Please pick a date and time slot.");
      return;
    }
    if (isBlocked) {
      toast.error("That date is unavailable — please pick another day.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/bookings", {
        service_id: product.id,
        service_name: qty > 1 ? `${product.name} x${qty}` : product.name,
        price_estimate: product.priceNum ? product.priceNum * qty : null,
        addons: [],
        vehicle: { model: form.vehicle },
        date,
        time_slot: slot,
        address: form.address,
        name: form.name,
        phone: form.phone,
        email: form.email,
        notes: form.notes,
        company: form.company,
      });
      setDone(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const inputLight = "w-full bg-white border border-gray-300 px-3.5 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#EA0A0B] transition-colors";

  return (
    <div data-testid="booking-modal" className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8 overflow-y-auto">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white my-auto">
        <button data-testid="booking-modal-close" onClick={onClose} aria-label="Close" className="absolute top-3 right-3 p-2 text-gray-400 hover:text-gray-900">
          <X className="w-5 h-5" />
        </button>

        {done ? (
          <div data-testid="booking-confirmation" className="p-8 text-center">
            <CheckCircle2 className="mx-auto w-12 h-12 text-green-600" />
            <h3 className="mt-4 font-display uppercase text-2xl text-gray-900">Booking request received!</h3>
            <p className="mt-2 text-sm text-gray-600">
              Thanks {form.name.split(" ")[0]} — a confirmation email is on its way to{" "}
              <span className="font-semibold">{form.email}</span>.
            </p>
            <div data-testid="booking-call-cta" className="mt-5 border border-gray-200 bg-gray-50 p-4">
              <p className="font-display uppercase text-gray-900">One last step — call to secure your slot</p>
              <p className="mt-1 text-xs text-gray-500">Your request for {date} at {slot} is in. Give us a quick call to lock it in with a $25 deposit.</p>
              <a
                data-testid="booking-call-button"
                href="tel:+19803588323"
                className="mt-3 flex items-center justify-center w-full bg-[#EA0A0B] hover:bg-[#C20A0B] text-white font-display uppercase tracking-[0.1em] text-sm py-3 transition-colors"
              >
                Call (980) 358-8323
              </a>
            </div>
            <p className="mt-3 font-mono text-xs text-gray-400">Booking ID: {done.id?.slice(0, 8)}</p>
            <button data-testid="booking-done-button" onClick={onClose} className="mt-6 bg-[#EA0A0B] hover:bg-[#C20A0B] text-white font-display uppercase tracking-[0.1em] text-xs px-10 py-3.5 transition-colors">
              Done
            </button>
          </div>
        ) : (
          <form data-testid="booking-modal-form" onSubmit={submit} className="p-7">
            <h3 className="font-display uppercase text-xl text-gray-900 pr-8">{product.name}</h3>
            <div className="mt-1 flex items-center justify-between">
              <p className="text-sm font-bold text-gray-900">{product.price}</p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">Qty:</span>
                <button type="button" data-testid="booking-qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="w-7 h-7 border border-gray-300 flex items-center justify-center text-gray-600 hover:border-gray-500">
                  <Minus className="w-3.5 h-3.5" />
                </button>
                <span data-testid="booking-qty" className="text-sm font-semibold text-gray-900 w-5 text-center">{qty}</span>
                <button type="button" data-testid="booking-qty-plus" onClick={() => setQty(Math.min(10, qty + 1))} className="w-7 h-7 border border-gray-300 flex items-center justify-center text-gray-600 hover:border-gray-500">
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</label>
                <input data-testid="booking-date-input" type="date" min={today} value={date} onChange={(e) => setDate(e.target.value)} className={`${inputLight} mt-1`} required />
                {isBlocked && <p data-testid="booking-date-blocked-error" className="mt-1 text-xs text-[#EA0A0B]">That day is unavailable — pick another date.</p>}
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Arrival window</label>
                <select data-testid="booking-slot-select" value={slot} onChange={(e) => setSlot(e.target.value)} className={`${inputLight} mt-1 disabled:opacity-50`} disabled={!date || isBlocked || slotsLoading || (daySlots !== null && daySlots.length === 0)} required>
                  <option value="">
                    {!date ? "Pick a date first" : slotsLoading ? "Checking times…" : isBlocked ? "Day unavailable" : daySlots && daySlots.length === 0 ? "Fully booked — pick another day" : "Select time…"}
                  </option>
                  {(daySlots || []).map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <input data-testid="booking-name-input" required value={form.name} onChange={set("name")} placeholder="Full name *" className={inputLight} />
              <input data-testid="booking-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="Phone *" className={inputLight} />
              <input data-testid="booking-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="Email *" className={`${inputLight} sm:col-span-2`} />
              <input data-testid="booking-address-input" required value={form.address} onChange={set("address")} placeholder="Service address (street, city, ZIP) *" className={`${inputLight} sm:col-span-2`} />
              <input data-testid="booking-vehicle-input" value={form.vehicle} onChange={set("vehicle")} placeholder="Vehicle (year / make / model)" className={`${inputLight} sm:col-span-2`} />
              <textarea data-testid="booking-notes-input" value={form.notes} onChange={set("notes")} placeholder="Notes (gate code, parking…)" rows={2} className={`${inputLight} sm:col-span-2 resize-none`} />
              <input type="text" name="company" value={form.company} onChange={set("company")} tabIndex={-1} autoComplete="off" className="jpd-honeypot" aria-hidden="true" />
            </div>

            <button
              data-testid="booking-submit-button"
              disabled={loading}
              className="mt-5 w-full bg-[#EA0A0B] hover:bg-[#C20A0B] disabled:opacity-60 text-white font-display uppercase tracking-[0.1em] text-sm py-3.5 transition-colors"
            >
              {loading ? "Booking…" : "Confirm Booking"}
            </button>
            <p className="mt-3 text-[11px] text-gray-400 text-center">Call (980) 358-8323 to secure your booking — $25 deposit by phone, balance after the job.</p>
          </form>
        )}
      </div>
    </div>
  );
};

export default function BookingPage() {
  const [params] = useSearchParams();
  const [category, setCategory] = useState("auto");
  const [sort, setSort] = useState("name-az");
  const [selected, setSelected] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]);
  const [shopProducts, setShopProducts] = useState(SHOP_PRODUCTS);

  useEffect(() => {
    api.get("/availability").then((res) => setBlockedDates(res.data.blocked_dates || [])).catch(() => {});
    api.get("/store").then((res) => {
      if (Array.isArray(res.data.products) && res.data.products.length) setShopProducts(res.data.products);
    }).catch(() => {});
    const svc = params.get("service");
    if (svc) {
      const found = SHOP_PRODUCTS.find((p) => p.id === svc || p.id.startsWith(svc));
      if (found) {
        setCategory(found.cat);
        setSelected(found);
      }
    }
  }, [params]);

  const products = useMemo(() => {
    const list = shopProducts.filter((p) => p.cat === category);
    if (sort === "price-asc") return [...list].sort((a, b) => (a.priceNum ?? Infinity) - (b.priceNum ?? Infinity));
    if (sort === "price-desc") return [...list].sort((a, b) => (b.priceNum ?? -1) - (a.priceNum ?? -1));
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [category, sort, shopProducts]);

  return (
    <div data-testid="booking-page" className="min-h-screen bg-white">
      <Navbar />

      <div className="pt-28 md:pt-32 pb-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 pb-6 border-b border-gray-200">
            <img src={LOGO_MARK} alt="Brad's Mobile Detailing" className="h-12 w-12 object-cover" />
            <div>
              <h1 className="font-display uppercase text-2xl md:text-3xl text-gray-900 tracking-wide">Brad's Mobile Detailing</h1>
              <p className="text-sm text-gray-500">Virtual Shop — Charlotte, NC and Surroundings</p>
            </div>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-4 sm:items-end sm:justify-between">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Category</label>
              <select
                data-testid="shop-category-select"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="mt-1 block w-full sm:w-72 bg-white border border-gray-300 px-3.5 py-2.5 text-sm text-gray-900 focus:outline-none focus:border-[#EA0A0B]"
              >
                {SHOP_CATEGORIES.map((c) => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Sort By</label>
              <select
                data-testid="shop-sort-select"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="mt-1 block w-full sm:w-48 bg-white border border-gray-300 px-3.5 py-2.5 text-sm text-gray-900 focus:outline-none focus:border-[#EA0A0B]"
              >
                <option value="name-az">Name A-Z</option>
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
              </select>
            </div>
          </div>

          <h2 className="mt-8 font-display uppercase text-xl text-gray-900 tracking-wide" data-testid="shop-category-title">
            {SHOP_CATEGORIES.find((c) => c.id === category)?.label}
          </h2>

          <div data-testid="shop-product-list" className="mt-5 space-y-5">
            {products.map((p) => (
              <ProductCard key={p.id} product={p} onBook={setSelected} />
            ))}
          </div>

          <p className="mt-10 text-xs text-gray-400 text-center">
            Questions before booking? <Link to="/contact-us" className="text-[#EA0A0B] hover:underline">Contact us</Link> — call or text anytime.
          </p>
        </div>
      </div>

      <Footer />
      <StickyCTA />
      {selected && <BookingModal product={selected} blockedDates={blockedDates} onClose={() => setSelected(null)} />}
    </div>
  );
}
